<template>
  <!--
    A voice note, playable in place.

    Not a bare <audio controls>: that widget is a different size, shape and
    colour in every browser, and it is the only control in the system that would
    not be ours. The element is still underneath doing the decoding — this is
    paint and a scrubber over it.
  -->
  <div class="audio" :class="{ 'audio--disabled': disabled }">
    <audio
      ref="element"
      :src="src"
      preload="metadata"
      @loadedmetadata="onLoadedMetadata"
      @durationchange="onDurationChange"
      @timeupdate="onTimeUpdate"
      @play="playing = true"
      @pause="playing = false"
      @ended="onEnded"
      @error="failed = true"
    />

    <button
      type="button"
      class="audio__play"
      :disabled="disabled || failed"
      :aria-label="playing ? uiLabel('audioPause') : uiLabel('audioPlay')"
      @click="toggle"
    >
      <svg v-if="playing" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" />
      </svg>
      <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M8 5.5v13a1 1 0 0 0 1.54.84l10-6.5a1 1 0 0 0 0-1.68l-10-6.5A1 1 0 0 0 8 5.5z" />
      </svg>
    </button>

    <input
      class="audio__seek"
      type="range"
      min="0"
      :max="Math.max(duration, 0.1)"
      step="0.01"
      :value="current"
      :disabled="disabled || failed || !duration"
      :aria-label="label || uiLabel('audioPlay')"
      @input="seek($event.target.valueAsNumber)"
    />

    <span class="audio__clock mono">{{ formatClock(current) }} / {{ formatClock(duration) }}</span>

    <button
      v-if="removable"
      type="button"
      class="audio__remove"
      :disabled="disabled"
      :aria-label="uiLabel('audioDelete')"
      @click="$emit('remove')"
    >
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
        <polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        <path d="M10 11v6M14 11v6" />
      </svg>
    </button>
  </div>
</template>

<script setup>
import { ref, watch, onBeforeUnmount } from 'vue'
import { formatClock } from '../utils/audio'
import { uiLabel } from '../utils/labels'

const props = defineProps({
  /** Where the bytes are. `api.urlWithToken('/files/' + id)` for a stored one. */
  src: { type: String, required: true },
  /** What a screen reader calls this particular clip. */
  label: { type: String, default: '' },
  removable: Boolean,
  disabled: Boolean,
})

defineEmits(['remove'])

const element = ref(null)
const playing = ref(false)
const failed = ref(false)
const current = ref(0)
const duration = ref(0)

// ── The duration that is not there ───────────────────────────────────────────
//
// A webm written by MediaRecorder carries no duration in its header — the
// encoder does not know it until the recording stops, and by then the header is
// written. Every browser therefore reports `Infinity` for a fresh recording,
// which renders as `Infinity:NaN` on the clock and leaves the scrubber dead.
//
// The one workaround there is: seek far past any possible end. The element is
// forced to walk the file, discovers the real length and fires `durationchange`
// with it, at which point we put the playhead back. Ugly, universally used, and
// invisible to the person watching.
let probing = false

function onLoadedMetadata() {
  const audio = element.value
  if (!audio) return
  if (Number.isFinite(audio.duration) && audio.duration > 0) {
    duration.value = audio.duration
    return
  }
  probing = true
  try { audio.currentTime = 1e101 } catch { probing = false }
}

function onDurationChange() {
  const audio = element.value
  if (!audio || !Number.isFinite(audio.duration) || audio.duration <= 0) return
  duration.value = audio.duration
  if (probing) {
    probing = false
    audio.currentTime = 0
    current.value = 0
  }
}

function onTimeUpdate() {
  // While probing the playhead is somewhere absurd; showing it would flash the
  // real length onto the clock as an elapsed time.
  if (probing) return
  current.value = element.value?.currentTime ?? 0
}

function onEnded() {
  playing.value = false
  current.value = 0
  if (element.value) element.value.currentTime = 0
}

function toggle() {
  const audio = element.value
  if (!audio) return
  if (audio.paused) audio.play().catch(() => { failed.value = true })
  else audio.pause()
}

function seek(seconds) {
  const audio = element.value
  if (!audio || !Number.isFinite(seconds)) return
  audio.currentTime = seconds
  current.value = seconds
}

// A new clip in the same slot: nothing of the old one may survive, or the
// scrubber sits at the previous recording's length.
watch(() => props.src, () => {
  playing.value = false
  failed.value = false
  probing = false
  current.value = 0
  duration.value = 0
})

onBeforeUnmount(() => {
  // Leaving the screen mid-playback must stop the sound, not orphan it.
  element.value?.pause()
})
</script>

<style scoped>
.audio {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius);
  background: var(--color-surface);
}
.audio--disabled { opacity: 0.55; }

.audio__play,
.audio__remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: none;
  cursor: pointer;
  transition: background var(--fast), color var(--fast);
}

.audio__play {
  width: 30px;
  height: 30px;
  border-radius: var(--radius-full);
  background: var(--color-primary);
  color: #fff;
}
.audio__play:hover:not(:disabled) { background: var(--color-primary-dark); }
.audio__play:disabled { background: var(--color-gray-300); cursor: not-allowed; }

.audio__remove {
  width: 26px;
  height: 26px;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--color-text-mute);
}
.audio__remove:hover:not(:disabled) { background: var(--color-danger-soft); color: var(--color-danger); }

.audio__seek {
  flex: 1;
  min-width: 60px;
  height: 4px;
  appearance: none;
  border-radius: var(--radius-full);
  background: var(--color-surface-2);
  cursor: pointer;
}
.audio__seek:disabled { cursor: default; }
.audio__seek::-webkit-slider-thumb {
  appearance: none;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: var(--color-primary);
  cursor: pointer;
}
.audio__seek::-moz-range-thumb {
  width: 12px;
  height: 12px;
  border: none;
  border-radius: 50%;
  background: var(--color-primary);
  cursor: pointer;
}

.audio__clock {
  flex-shrink: 0;
  font-size: var(--text-xs);
  color: var(--color-text-soft);
  font-variant-numeric: tabular-nums;
}
</style>
